import React, { useEffect, useMemo, useState } from "react";
import { Grid, Typography } from "@mui/material";
import SectionPanel from "./SectionPanel";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
} from "react-hook-form";
import Select from "@/components/Select";
import TreatmentAgenciesService, {
  TreatmentAgencyAddress,
} from "@/services/treatment-agencies.service";
import { InviteFormData } from "./validation.schema";

/**
 * PRET-1837 — the participant's treatment service assignment (Hi-Fi screen 8a, sec-1).
 *
 * Shared by the invite and edit modals so the two cannot drift. The agency list is loaded
 * once the modal opens; the location list is loaded per agency, which is what makes it
 * "filtered to the selected agency" rather than a list of ~900 entries.
 *
 * Changing the agency clears any location already chosen: a location belongs to exactly one
 * agency, so a retained one would pair a location with an agency it does not belong to. The
 * server rejects that pairing as well — this only keeps the form from offering it.
 */

interface Props {
  control: Control<InviteFormData>;
  errors: FieldErrors<InviteFormData>;
  setValue: UseFormSetValue<InviteFormData>;
  accountId?: string;
  userId?: string;
  /** Modal open state — the agency list is fetched when the modal opens, not on mount. */
  open: boolean;
  selectedAgencyId?: string;
  selectedLocationId?: string;
  /**
   * Reports the resolved names upward. The design echoes agency and location inside the FIRST
   * APPOINTMENT section, not here, so the parent needs the display names.
   */
  onNamesResolved?: (names: {
    agencyName?: string;
    locationName?: string;
  }) => void;
}

const TreatmentServiceAssignment: React.FC<Props> = ({
  control,
  errors,
  setValue,
  accountId,
  userId,
  open,
  selectedAgencyId,
  selectedLocationId,
  onNamesResolved,
}) => {
  const [agencies, setAgencies] = useState<{ key: string; value: string }[]>(
    []
  );
  const [addresses, setAddresses] = useState<TreatmentAgencyAddress[]>([]);
  const [isLoadingAgencies, setIsLoadingAgencies] = useState(false);
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(false);

  useEffect(() => {
    const loadAgencies = async () => {
      if (!userId || !accountId || !open) return;
      setIsLoadingAgencies(true);
      try {
        const { agencies } =
          await TreatmentAgenciesService.getTreatmentAgencies({
            accountId,
            userId,
          });
        setAgencies(
          agencies.map((agency) => ({ key: agency._id, value: agency.name }))
        );
      } catch (error) {
        console.error("Error fetching treatment agencies", error);
        setAgencies([]);
      } finally {
        setIsLoadingAgencies(false);
      }
    };

    loadAgencies();
  }, [userId, accountId, open]);

  useEffect(() => {
    const loadAddresses = async () => {
      if (!userId || !accountId || !selectedAgencyId) {
        setAddresses([]);
        return;
      }
      setIsLoadingAddresses(true);
      try {
        const { addresses } =
          await TreatmentAgenciesService.getTreatmentAgencyAddresses({
            accountId,
            userId,
            agencyId: selectedAgencyId,
          });
        setAddresses(addresses);
      } catch (error) {
        console.error("Error fetching treatment agency addresses", error);
        setAddresses([]);
      } finally {
        setIsLoadingAddresses(false);
      }
    };

    loadAddresses();
  }, [selectedAgencyId, userId, accountId]);

  const addressOptions = useMemo(
    () =>
      addresses.map((address) => ({ key: address._id, value: address.name })),
    [addresses]
  );

  const agencyName = useMemo(
    () => agencies.find((agency) => agency.key === selectedAgencyId)?.value,
    [agencies, selectedAgencyId]
  );

  const locationName = useMemo(
    () => addresses.find((address) => address._id === selectedLocationId)?.name,
    [addresses, selectedLocationId]
  );

  useEffect(() => {
    onNamesResolved?.({ agencyName, locationName });
    // Intentionally keyed on the names only — including the callback would loop.
  }, [agencyName, locationName]);

  // An agency can legitimately have no locations recorded. Saying so is better than leaving an
  // empty dropdown the coordinator cannot get past and cannot explain.
  const agencyHasNoLocations =
    !!selectedAgencyId && !isLoadingAddresses && addresses.length === 0;

  return (
    <SectionPanel
      title="Treatment service assignment"
      subtitle="The participant will attend here. They cannot change it in the app."
    >
      <Grid item xs={12} sm={6}>
        <Controller
          name="treatmentAgencyId"
          control={control}
          render={({ field }) => (
            <Select
              handleChange={(value) => {
                field.onChange(value);
                // A location belongs to one agency only, so it cannot survive the change.
                setValue("locationId", "");
              }}
              data={agencies}
              value={(field.value as string) ?? ""}
              label="Treatment agency"
              required
              id="client-treatment-agency"
              fullWidth
              disabled={isLoadingAgencies}
              error={!!errors.treatmentAgencyId}
              errorMessage={errors.treatmentAgencyId?.message as string}
            />
          )}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <Controller
          name="locationId"
          control={control}
          render={({ field }) => (
            <Select
              handleChange={(value) => field.onChange(value)}
              data={addressOptions}
              value={(field.value as string) ?? ""}
              label="Location"
              required
              id="client-treatment-location"
              fullWidth
              disabled={!selectedAgencyId || isLoadingAddresses}
              error={!!errors.locationId || agencyHasNoLocations}
              errorMessage={
                agencyHasNoLocations
                  ? "This agency has no locations configured. Contact an administrator."
                  : (errors.locationId?.message as string)
              }
            />
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <Typography variant="caption" color="text.secondary">
          Filtered to the selected agency.
        </Typography>
      </Grid>
    </SectionPanel>
  );
};

export default TreatmentServiceAssignment;
